import { fg } from '@atlaskit/platform-feature-flags';
import type { ConversationChannel } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { generateConversationName } from '@atlassian/conversation-assistant-service/services/assistance-service/utils/conversation';
import {
	CHAT_STORE_STATUS,
	type Conversation,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

/**
 * Merges conversation data from local state and API response with network priority.
 * Prioritizes fields from the API (channelsFromAPI) as they represent the latest server state.
 * Falls back to local state (localConversation) for fields missing in the API response.
 */
export const mergeConversationWithNetworkPriority = (
	remoteConversation: ConversationChannel,
	localConversation?: Conversation,
): Conversation => {
	return {
		id: remoteConversation.id,

		// Prioritize API data if present, otherwise use local or generate new
		name: remoteConversation.name ?? localConversation?.name ?? generateConversationName(),
		timeCreated: remoteConversation.time_created ?? localConversation?.timeCreated,
		lastMessage: remoteConversation.last_message ?? localConversation?.lastMessage ?? undefined,
		lastMessageType:
			remoteConversation.last_message_type ?? localConversation?.lastMessageType ?? [],
		selectedAgentIds: localConversation?.selectedAgentIds ?? [],
		lastMessageTimestamp:
			remoteConversation.last_message_timestamp ?? localConversation?.lastMessageTimestamp,
		lastAgentMessageStatus:
			remoteConversation.last_agent_message_status ?? localConversation?.lastAgentMessageStatus,
		readStatus: remoteConversation.readStatus ?? localConversation?.readStatus,
		tags: remoteConversation.tags ?? localConversation?.tags,

		// Use local state data if it exists (messages, status are maintained locally)
		messages: localConversation?.messages ?? [],
		status: localConversation?.status ?? CHAT_STORE_STATUS.idle,
		traces: localConversation?.traces ?? [],

		...(localConversation?.sourceAgentId ? { sourceAgentId: localConversation.sourceAgentId } : {}),
		...(localConversation?.sourceId ? { sourceId: localConversation.sourceId } : {}),
		// Preserve runtime abort controller so Stop button works after channels rehydration (e.g. refocus)
		...(localConversation?.storeAbortController != null
			? { storeAbortController: localConversation.storeAbortController }
			: {}),
		...(localConversation?.rovoSpaceId ? { rovoSpaceId: localConversation.rovoSpaceId } : {}),
		// `agent` is optional on ConversationChannel, so the server-sourced channel can
		// omit it. Under rovo_chat_agent_selection the header derives its displayed
		// agent (name/avatar) ONLY from `conversation.agent`, so taking the remote
		// agent verbatim would overwrite a locally-seeded agent with `undefined` and
		// drop a specialist back to default Rovo (e.g. opening an existing chat with a
		// specific agent via URL params, then a channel rehydration on init/refocus).
		// Gate-on therefore falls back to the locally-seeded agent when the remote has
		// none; gate-off keeps the master baseline of taking the remote agent verbatim.
		// Cleanup: drop the gate when launched.
		agent: fg('rovo_chat_agent_selection')
			? (remoteConversation.agent ?? localConversation?.agent)
			: remoteConversation.agent,
		scopes: remoteConversation.scopes ?? localConversation?.scopes,
	};
};
